import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import CardItem from '../profile/CardItem';
import '../../styles/football-theme.css';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Multi-pack opening: show each pack's cards in sequence, with Next pack / Skip.
 * On Skip or when all packs shown: display summary (X cards assigned) + "View in Profile".
 */
const MultiPackOpening = ({
  packOpenings,
  totalCards,
  totalPacks,
  packName,
  user,
  onClose,
  onViewInProfile
}) => {
  const { t } = useLanguage();
  const [currentPackIndex, setCurrentPackIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showCard, setShowCard] = useState(true);

  useEffect(() => {
    document.body.classList.add('pack-opening-modal-open');
    return () => {
      document.body.classList.remove('pack-opening-modal-open');
    };
  }, []);

  const currentOpening = packOpenings[currentPackIndex];
  const currentCards = currentOpening?.cards || [];
  const currentCard = currentCards[currentCardIndex];
  const isLastPack = currentPackIndex >= packOpenings.length - 1;
  const isLastCardInPack = currentCardIndex >= currentCards.length - 1;

  const goToSummary = () => {
    setShowSummary(true);
  };

  const handleNext = () => {
    if (showSummary) return;
    if (currentCards.length === 0) {
      if (isLastPack) goToSummary();
      else setCurrentPackIndex((i) => i + 1);
      return;
    }
    if (!isLastCardInPack) {
      setShowCard(false);
      setTimeout(() => {
        setCurrentCardIndex((i) => i + 1);
        setShowCard(true);
      }, 300);
    } else {
      if (isLastPack) {
        goToSummary();
      } else {
        setCurrentPackIndex((i) => i + 1);
        setCurrentCardIndex(0);
      }
    }
  };

  const handlePrevious = () => {
    if (showSummary) return;
    if (currentCardIndex > 0) {
      setShowCard(false);
      setTimeout(() => {
        setCurrentCardIndex((i) => i - 1);
        setShowCard(true);
      }, 300);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (!packOpenings || packOpenings.length === 0) {
    return null;
  }

  if (showSummary) {
    return (
      <div className="fixed inset-0 z-[2147483000] flex items-center justify-center pt-28 pb-4 px-4 bg-black/80 backdrop-blur-sm">
        <div className="football-card glass-football p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <p className="text-xl text-white font-semibold mb-2">
              {t('marketplaceCards.cardsAssignedSummary').replace('{count}', totalCards)}
            </p>
            <p className="text-gray-400 text-sm">
              {totalPacks} {totalPacks === 1 ? 'pack' : 'packs'} opened
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onViewInProfile}
              className="btn-football px-6 py-3"
            >
              {t('marketplaceCards.viewInProfile')}
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 rounded-lg font-semibold transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[2147483000] flex items-center justify-center pt-28 pb-4 px-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-4xl relative flex justify-center">
        <div className="football-card glass-football pt-3 px-5 pb-5 text-center w-fit max-w-full relative">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 z-10 w-9 h-9 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-lg"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-2">
            <h2 className="text-xl font-bold text-white mb-0.5">{packName}</h2>
            <p className="text-gray-300 text-sm">
              Pack {currentPackIndex + 1} of {packOpenings.length}
              {currentCards.length > 0 && (
                <> · Card {currentCardIndex + 1} of {currentCards.length}</>
              )}
            </p>
          </div>

          {currentCards.length > 0 && currentCard ? (
            <>
              <div className="pack-container mb-4 profile-container" style={{ minHeight: '452px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {showCard ? (
                  <div className="pack-card flip">
                    <div className="pack-card-front">
                      <div className="profile-gallery-card-scaled">
                        <CardItem
                          card={currentCard}
                          user={user || {}}
                          hideHoverActions
                          isUsedInActiveGameweek={false}
                          onCardListed={() => {}}
                          hasOpenTrade={false}
                          openTrade={null}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="profile-gallery-card-scaled opacity-0 pointer-events-none">
                    <div className="profile-card-item football-card glass-football w-[303px] h-[452px] min-w-[303px] min-h-[452px] bg-gray-700 rounded" />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                <button
                  onClick={handlePrevious}
                  disabled={currentCardIndex === 0}
                  className="btn-football px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← {t('common.previous')}
                </button>
                <div className="scoreboard inline-block px-6 py-3">
                  <div className="text-xl font-bold text-yellow-400">
                    {currentCardIndex + 1} / {currentCards.length}
                  </div>
                </div>
                <button onClick={handleNext} className="btn-football px-6 py-3">
                  {isLastCardInPack && isLastPack ? 'Finish →' : isLastCardInPack ? t('marketplaceCards.nextPack') : t('common.next')} →
                </button>
                <button
                  onClick={goToSummary}
                  className="px-6 py-3 border-2 border-amber-500 text-amber-400 hover:bg-amber-500/20 rounded-lg font-semibold transition-colors"
                >
                  {t('marketplaceCards.skipOpening')}
                </button>
              </div>

              <div className="mt-4 w-full max-w-md mx-auto">
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${totalCards > 0 ? ((currentPackIndex * (currentCards.length || 1) + currentCardIndex + 1) / totalCards) * 100 : 0}%`
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="py-8">
              <p className="text-gray-400 mb-4">No cards in this pack.</p>
              <div className="flex justify-center gap-3">
                <button onClick={handleNext} className="btn-football px-6 py-3">
                  {isLastPack ? t('marketplaceCards.viewInProfile') : t('marketplaceCards.nextPack')} →
                </button>
                <button
                  onClick={goToSummary}
                  className="px-6 py-3 border-2 border-amber-500 text-amber-400 hover:bg-amber-500/20 rounded-lg font-semibold"
                >
                  {t('marketplaceCards.skipOpening')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiPackOpening;
